/**
 * Domain types for billing.
 *
 * Owned by us — not generated from Supabase or Stripe.
 * Components import from here. The service layer (src/lib/api/billing.ts)
 * maps external data → these types.
 *
 * Migration note (Phase 2): when Magdy wires the real Stripe backend,
 * only src/lib/api/billing.ts changes. These types stay.
 */

/** Plans the app recognises. Matches the DB check constraint in user_profiles. */
export type Plan = "free" | "pro";

/**
 * Result of a checkout session creation.
 * In mock mode: url is an internal route.
 * In live mode: url is a Stripe-hosted checkout URL.
 */
export interface CheckoutSession {
  url: string;
}

/**
 * Result of a billing portal session creation.
 * In mock mode: url is an internal route (settings page).
 * In live mode: url is the Stripe billing portal URL.
 */
export interface PortalSession {
  url: string;
}

/**
 * Generic billing result — matches the pattern used by auth.ts and projects.ts.
 * Discriminated union so callers must handle both branches.
 */
export type BillingResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: BillingError };

export type BillingErrorCode =
  | "unauthenticated"
  | "network"
  | "project_limit_reached"
  | "mock_only_violation"
  | "unknown";

export interface BillingError {
  code: BillingErrorCode;
  message: string;
}

/**
 * Plan limits — single source of truth so the UI and service layer
 * show the same numbers.
 */
export const PLAN_LIMITS = {
  free: {
    maxCloudProjects: 3,
    maxPartsPerProject: 30,
    watermarkedPdf: true,
  },
  pro: {
    maxCloudProjects: Infinity,
    maxPartsPerProject: Infinity,
    watermarkedPdf: false,
  },
} as const satisfies Record<Plan, PlanLimits>;

export interface PlanLimits {
  maxCloudProjects: number;
  maxPartsPerProject: number;
  watermarkedPdf: boolean;
}
